package chand.wordvoyage

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.scaleIn
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.foundation.Image
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.scale
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.viewmodel.compose.viewModel
import chand.wordvoyage.config.OpenAIConfig
import chand.wordvoyage.repository.FirestoreRepository
import chand.wordvoyage.repository.OpenAIRepository
import chand.wordvoyage.ui.AdBanner
import chand.wordvoyage.ui.WordVoyageTopBar
import chand.wordvoyage.viewmodel.WordGenerationState
import chand.wordvoyage.viewmodel.WordViewModel
import org.jetbrains.compose.resources.painterResource
import wordvoyage.shared.generated.resources.Res
import wordvoyage.shared.generated.resources.ic_book
import wordvoyage.shared.generated.resources.ic_sparkle
import wordvoyage.shared.generated.resources.ic_target
import wordvoyage.shared.generated.resources.ic_refresh
import wordvoyage.shared.generated.resources.ic_warning

// Background gradient colours
private val GradientTop    = Color(0xFF6200EA)
private val GradientMid    = Color(0xFF0277BD)
private val GradientBottom = Color(0xFF00695C)

@Composable
fun GenerateWordScreen(
    onShowWordDetail: (String) -> Unit,
    onNavigateToHistory: () -> Unit = {},
    onLogout: () -> Unit = {},
    onDeleteAccount: () -> Unit = {},
    onViewPrivacyPolicy: () -> Unit = {},
    onViewChildrensPrivacy: () -> Unit = {},
    onViewDataControlDeletion: () -> Unit = {},
    userId: String = "",
    firestoreRepository: FirestoreRepository? = null
) {
    val repository = remember { OpenAIRepository(OpenAIConfig.API_KEY) }
    val viewModel: WordViewModel = viewModel {
        WordViewModel(repository, firestoreRepository, userId)
    }

    val state = viewModel.state.collectAsState()
    val isWordAvailable = viewModel.isWordAvailable.collectAsState()

    Scaffold(
        topBar = {
            WordVoyageTopBar(
                title = "Word Voyage",
                onNavigateToHistory = onNavigateToHistory,
                onLogout = onLogout,
                onDeleteAccount = onDeleteAccount,
                onViewPrivacyPolicy = onViewPrivacyPolicy,
                onViewChildrensPrivacy = onViewChildrensPrivacy,
                onViewDataControlDeletion = onViewDataControlDeletion
            )
        },
        bottomBar = { AdBanner() },
        containerColor = Color.Transparent
    ) { innerPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    brush = Brush.verticalGradient(
                        colors = listOf(GradientTop, GradientMid, GradientBottom)
                    )
                )
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(innerPadding)
                    .verticalScroll(rememberScrollState())
                    .padding(24.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {

                // ── App tagline ──────────────────────────────────────────
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center
                ) {
                    Icon(
                        painter = painterResource(Res.drawable.ic_sparkle),
                        contentDescription = null,
                        tint = Color.White,
                        modifier = Modifier.size(24.dp)
                    )
                    Spacer(modifier = Modifier.size(10.dp))
                    Text(
                        text = "Discover a New Word",
                        style = MaterialTheme.typography.headlineSmall,
                        color = Color.White,
                        textAlign = TextAlign.Center
                    )
                }
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = "Expand your vocabulary every day",
                    style = MaterialTheme.typography.bodyMedium,
                    fontStyle = FontStyle.Italic,
                    color = Color.White.copy(alpha = 0.8f),
                    textAlign = TextAlign.Center
                )

                Spacer(modifier = Modifier.height(48.dp))

                // ── State area ───────────────────────────────────────────
                when (val currentState = state.value) {
                    is WordGenerationState.Idle -> {
                        // Decorative circle hint
                        Box(
                            modifier = Modifier
                                .size(160.dp)
                                .background(
                                    color = Color.White.copy(alpha = 0.10f),
                                    shape = CircleShape
                                ),
                            contentAlignment = Alignment.Center
                        ) {
                            Image(
                                painter = painterResource(Res.drawable.ic_book),
                                contentDescription = "Book Icon",
                                modifier = Modifier.size(80.dp)
                            )
                        }
                        Spacer(modifier = Modifier.height(32.dp))
                        Text(
                            text = "Tap below to discover\nyour next word",
                            style = MaterialTheme.typography.bodyLarge,
                            color = Color.White.copy(alpha = 0.9f),
                            textAlign = TextAlign.Center,
                            lineHeight = 24.sp
                        )
                    }

                    is WordGenerationState.Loading -> {
                        val pulse = rememberInfiniteTransition(label = "pulse")
                        val scale by pulse.animateFloat(
                            initialValue = 0.92f,
                            targetValue = 1.08f,
                            animationSpec = infiniteRepeatable(
                                animation = tween(700),
                                repeatMode = RepeatMode.Reverse
                            ),
                            label = "scale"
                        )
                        Box(
                            modifier = Modifier
                                .size(160.dp)
                                .scale(scale)
                                .background(
                                    color = Color.White.copy(alpha = 0.12f),
                                    shape = CircleShape
                                ),
                            contentAlignment = Alignment.Center
                        ) {
                            CircularProgressIndicator(
                                color = Color.White,
                                strokeWidth = 4.dp,
                                modifier = Modifier.size(60.dp)
                            )
                        }
                        Spacer(modifier = Modifier.height(24.dp))
                        Text(
                            text = "Generating your word...",
                            style = MaterialTheme.typography.bodyLarge,
                            color = Color.White.copy(alpha = 0.9f)
                        )
                    }

                    is WordGenerationState.Success -> {
                        AnimatedVisibility(
                            visible = true,
                            enter = fadeIn(tween(400)) + scaleIn(tween(400))
                        ) {
                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.spacedBy(16.dp)
                            ) {
                                // Word card
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .shadow(12.dp, RoundedCornerShape(24.dp))
                                        .background(
                                            color = Color.White,
                                            shape = RoundedCornerShape(24.dp)
                                        )
                                        .padding(horizontal = 32.dp, vertical = 28.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                        Row(
                                            verticalAlignment = Alignment.CenterVertically,
                                            horizontalArrangement = Arrangement.Center
                                        ) {
                                            Icon(
                                                painter = painterResource(Res.drawable.ic_target),
                                                contentDescription = null,
                                                tint = GradientTop.copy(alpha = 0.7f),
                                                modifier = Modifier.size(18.dp)
                                            )
                                            Spacer(modifier = Modifier.size(8.dp))
                                            Text(
                                                text = "Word of the moment",
                                                style = MaterialTheme.typography.labelLarge,
                                                color = MaterialTheme.colorScheme.primary.copy(alpha = 0.7f),
                                                letterSpacing = 1.sp
                                            )
                                        }
                                        Spacer(modifier = Modifier.height(10.dp))
                                        Text(
                                            text = currentState.word,
                                            style = MaterialTheme.typography.displayMedium,
                                            color = MaterialTheme.colorScheme.primary,
                                            textAlign = TextAlign.Center,
                                            letterSpacing = 1.sp
                                        )
                                    }
                                }

                                // Get Detail Info button
                                if (isWordAvailable.value) {
                                    Button(
                                        onClick = { onShowWordDetail(currentState.word) },
                                        colors = ButtonDefaults.buttonColors(
                                            containerColor = MaterialTheme.colorScheme.surface,
                                            contentColor = MaterialTheme.colorScheme.primary
                                        ),
                                        shape = RoundedCornerShape(50.dp),
                                        modifier = Modifier
                                            .fillMaxWidth(0.7f)
                                            .height(52.dp)
                                    ) {
                                        Text(
                                            text = "Get Detail Info",
                                            style = MaterialTheme.typography.titleMedium
                                        )
                                    }
                                }
                            }
                        }
                    }

                    is WordGenerationState.Error -> {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(
                                    Color.White.copy(alpha = 0.15f),
                                    RoundedCornerShape(16.dp)
                                )
                                .padding(16.dp)
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.Center
                            ) {
                                Icon(
                                    painter = painterResource(Res.drawable.ic_warning),
                                    contentDescription = null,
                                    tint = Color.White,
                                    modifier = Modifier.size(20.dp)
                                )
                                Spacer(modifier = Modifier.size(10.dp))
                                Text(
                                    text = currentState.message,
                                    fontSize = 15.sp,
                                    color = Color.White,
                                    textAlign = TextAlign.Center
                                )
                            }
                        }
                    }
                }

                Spacer(modifier = Modifier.height(48.dp))

                // ── Generate button ──────────────────────────────────────
                Button(
                    onClick = { viewModel.generateWord() },
                    enabled = state.value !is WordGenerationState.Loading,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        contentColor = MaterialTheme.colorScheme.primary,
                        disabledContainerColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.3f),
                        disabledContentColor = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.5f)
                    ),
                    shape = RoundedCornerShape(50.dp),
                    modifier = Modifier
                        .fillMaxWidth(0.75f)
                        .height(56.dp)
                        .shadow(8.dp, RoundedCornerShape(50.dp))
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.Center
                    ) {
                        Icon(
                            painter = painterResource(
                                if (state.value is WordGenerationState.Success)
                                    Res.drawable.ic_refresh else Res.drawable.ic_sparkle
                            ),
                            contentDescription = null,
                            modifier = Modifier.size(24.dp)
                        )
                        Spacer(modifier = Modifier.size(12.dp))
                        Text(
                            text = if (state.value is WordGenerationState.Success)
                                "Generate Another" else "Generate a Word",
                            style = MaterialTheme.typography.titleMedium
                        )
                    }
                }
            }
        }
    }
}


